import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../api/apiClient';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getTransactionCurrency, totalsByCurrency } from '../utils/reportCurrencyUtils';
import { categoryReportAllocations, displayedReportTransactions, reportTotalsByCurrency, transactionTypeLabel } from '../utils/reportPresentationUtils';

const PERIODS = [
  { value: 'today', label: 'Этот день' },
  { value: 'this_week', label: 'Эта неделя' },
  { value: 'this_month', label: 'Этот месяц' },
  { value: 'this_year', label: 'Этот год' },
  { value: 'last_20', label: 'Последние 20 записей' },
  { value: 'last_7_days', label: 'Последние 7 дней' },
  { value: 'last_30_days', label: 'Последние 30 дней' },
  { value: 'last_6_months', label: 'Последние 6 месяцев' },
  { value: 'last_month', label: 'Прошлый месяц' },
  { value: 'last_year', label: 'Прошлый год' },
  { value: 'all_time', label: 'Все время' },
  { value: 'custom', label: 'Другой период' }
];

const AMOUNT_MODES = [
  { value: 'any', label: 'Любая' },
  { value: 'eq', label: 'Равно' },
  { value: 'gt', label: 'Больше' },
  { value: 'lt', label: 'Меньше' },
  { value: 'between', label: 'Диапазон' }
];

// Flat checkbox list — for tags, locations
const CheckboxList = ({ label, value, onChange, options }) => {
  const toggle = (val) =>
    onChange(value.includes(val) ? value.filter(v => v !== val) : [...value, val]);

  return (
    <div className="w-full">
      {label && (
        <p className="mb-2 text-xs uppercase tracking-[0.24em] text-muted">{label}</p>
      )}
      <div className="mb-2 flex items-center gap-2 text-xs">
        <button type="button" className="font-medium text-accent" onClick={() => onChange(options.map(o => o.value))}>
          Выбрать все
        </button>
        <span className="text-muted">/</span>
        <button type="button" className="font-medium text-accent" onClick={() => onChange([])}>
          Снять все
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {options.length === 0 && (
          <p className="px-2 text-xs italic text-muted">Нет данных</p>
        )}
        {options.map(opt => (
          <label key={opt.value} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-base">
            <input
              type="checkbox"
              checked={value.includes(opt.value)}
              onChange={() => toggle(opt.value)}
              className="h-4 w-4 rounded accent-[var(--accent-0)]"
            />
            <span className="text-sm text-ink">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Grouped checkbox list — for categories with parent/child hierarchy
const GroupedCategoryCheckboxList = ({ label, value, onChange, categories }) => {
  const parents = categories.filter(c => !c.parent_id);
  const childrenByParent = {};
  categories.forEach(c => {
    if (c.parent_id) (childrenByParent[c.parent_id] ??= []).push(c);
  });

  const toggle = (id) => {
    const sid = String(id);
    onChange(value.includes(sid) ? value.filter(v => v !== sid) : [...value, sid]);
  };

  const toggleGroup = (parent) => {
    const children = childrenByParent[parent.id] ?? [];
    const groupIds = [String(parent.id), ...children.map(c => String(c.id))];
    const allSelected = groupIds.every(id => value.includes(id));
    onChange(allSelected
      ? value.filter(v => !groupIds.includes(v))
      : [...value, ...groupIds.filter(id => !value.includes(id))]
    );
  };

  const allIds = categories.map(c => String(c.id));

  return (
    <div className="w-full">
      {label && (
        <p className="mb-2 text-xs uppercase tracking-[0.24em] text-muted">{label}</p>
      )}
      <div className="mb-2 flex items-center gap-2 text-xs">
        <button type="button" className="font-medium text-accent" onClick={() => onChange(allIds)}>
          Выбрать все
        </button>
        <span className="text-muted">/</span>
        <button type="button" className="font-medium text-accent" onClick={() => onChange([])}>
          Снять все
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {parents.length === 0 && (
          <p className="px-2 text-xs italic text-muted">Нет данных</p>
        )}
        {parents.map(parent => {
          const children = childrenByParent[parent.id] ?? [];
          const groupIds = [String(parent.id), ...children.map(c => String(c.id))];
          const allSelected = groupIds.every(id => value.includes(id));
          const someSelected = groupIds.some(id => value.includes(id));
          return (
            <div key={parent.id}>
              {/* Parent row */}
              <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-base">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={() => toggleGroup(parent)}
                  className="h-4 w-4 rounded accent-[var(--accent-0)]"
                />
                <span className="text-sm font-semibold text-ink">{parent.name}</span>
              </label>
              {/* Child rows */}
              {children.map(child => (
                <label key={child.id} className="flex cursor-pointer items-center gap-3 rounded-lg py-1.5 pl-9 pr-2 transition-colors hover:bg-base">
                  <input
                    type="checkbox"
                    checked={value.includes(String(child.id))}
                    onChange={() => toggle(child.id)}
                    className="h-4 w-4 rounded accent-[var(--accent-0)]"
                  />
                  <span className="text-sm text-ink">{child.name}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ReportsPage = () => {
  const [format, setFormat] = useState('detailed');
  const [whatToShow, setWhatToShow] = useState('all');
  const [excludeTransfers, setExcludeTransfers] = useState(true);
  const [period, setPeriod] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [locationMode, setLocationMode] = useState('all');
  const [locationIds, setLocationIds] = useState([]);
  const [categoryMode, setCategoryMode] = useState('all');
  const [categoryIds, setCategoryIds] = useState([]);
  const [tagMode, setTagMode] = useState('all');
  const [tagIds, setTagIds] = useState([]);

  const [comment, setComment] = useState('');
  const [amountMode, setAmountMode] = useState('any');
  const [amountValue, setAmountValue] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const [locationsRes, categoriesRes, tagsRes] = await Promise.all([
          apiClient.get('/locations'),
          apiClient.get('/categories'),
          apiClient.get('/tags')
        ]);
        setLocations(locationsRes.data.data || []);
        setCategories(categoriesRes.data.data || []);
        setTags(tagsRes.data.data || []);
      } catch (err) {
        console.error('Error fetching reference data:', err);
      }
    };

    fetchReferences();
  }, []);

  useEffect(() => {
    if (locationMode === 'all' && locationIds.length) {
      setLocationIds([]);
    }
  }, [locationMode, locationIds]);

  useEffect(() => {
    if (categoryMode === 'all' && categoryIds.length) {
      setCategoryIds([]);
    }
  }, [categoryMode, categoryIds]);

  useEffect(() => {
    if (tagMode === 'all' && tagIds.length) {
      setTagIds([]);
    }
  }, [tagMode, tagIds]);

  useEffect(() => {
    if (whatToShow === 'transfer' && format !== 'detailed') {
      setFormat('detailed');
    }
    if (whatToShow === 'all' && format !== 'detailed') {
      setFormat('detailed');
    }
    if (whatToShow === 'income' && format === 'by_category') {
      setFormat('detailed');
    }
    if (whatToShow === 'expense' && format === 'by_income_source') {
      setFormat('detailed');
    }
  }, [whatToShow, format]);


  const locationOptions = useMemo(() => {
    return locations.map((loc) => ({
      value: String(loc.id),
      label: loc.name
    }));
  }, [locations]);

  const tagOptions = useMemo(() => {
    return tags.map((tag) => ({
      value: String(tag.id),
      label: tag.name
    }));
  }, [tags]);

  const displayedTransactions = useMemo(
    () => displayedReportTransactions(transactions, whatToShow, excludeTransfers),
    [transactions, whatToShow, excludeTransfers]
  );

  const groupedByDate = useMemo(() => {
    const groups = new Map();
    displayedTransactions.forEach((tx) => {
      const dateKey = tx.date ? tx.date.split('T')[0] : 'unknown';
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey).push(tx);
    });
    return Array.from(groups.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [displayedTransactions]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setExpandedCategoryId(null);
    try {
      const resolvedLocationIds = locationMode === 'all' ? [] : locationIds;
      const resolvedCategoryIds = categoryMode === 'all' ? [] : categoryIds;
      const resolvedTagIds = tagMode === 'all' ? [] : tagIds;

      const payload = {
        format,
        type: whatToShow === 'all' ? undefined : whatToShow,
        period,
        startDate: period === 'custom' ? startDate : undefined,
        endDate: period === 'custom' ? endDate : undefined,
        locationMode,
        locationIds: resolvedLocationIds,
        categoryMode,
        categoryIds: resolvedCategoryIds,
        tagMode,
        tagIds: resolvedTagIds,
        comment,
        amountMode,
        amountValue: amountValue !== '' ? Number(amountValue) : undefined,
        amountMin: amountMin !== '' ? Number(amountMin) : undefined,
        amountMax: amountMax !== '' ? Number(amountMax) : undefined
      };

      const response = await apiClient.post('/reports/advanced', payload);
      const data = response.data?.data || {};
      setTransactions(data.transactions || []);
      setSummary(data.summary || []);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.response?.data?.message || 'Ошибка при формировании отчета');
    } finally {
      setLoading(false);
    }
  };

  const renderSource = (tx) => {
    if (tx.type === 'income') {
      return tx.income_source_name || tx.to_location_name || '—';
    }
    if (tx.type === 'expense') {
      return tx.from_location_name || '—';
    }
    if (tx.type === 'transfer') {
      return `${tx.from_location_name || '—'} → ${tx.to_location_name || '—'}`;
    }
    return '—';
  };

  const categoryTransactions = useMemo(() => transactions, [transactions]);

  const totalAmounts = useMemo(() => {
    if (format !== 'by_category') return totalsByCurrency(transactions);
    const totals = new Map();
    summary.forEach((row) => {
      const currency = row.currency || '—';
      totals.set(currency, (totals.get(currency) || 0) + Number(row.total_amount || 0));
    });
    return Array.from(totals, ([currency, amount]) => ({ currency, amount }));
  }, [transactions, summary, format]);
  const reportTotals = useMemo(
    () => reportTotalsByCurrency(transactions, !excludeTransfers),
    [transactions, excludeTransfers]
  );

  const renderTransactionTitle = (tx) => transactionTypeLabel(tx.type) || tx.category_name || 'Без категории';

  return (
    <div className="space-y-10">
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-4xl font-semibold text-ink">Отчеты</h2>
        <div className="rounded-2xl border border-line bg-surface px-4 py-2 text-sm text-ink self-start sm:self-auto">
          {whatToShow === 'all' ? (
            reportTotals.length ? reportTotals.map(({ currency, income, expense, loanPayment, transfer, net }) => (
              <div key={currency} className="mb-1 last:mb-0">
                <span className="font-semibold">{currency}:</span> Доходы {income.toFixed(2)} · Расходы {expense.toFixed(2)} · Платежи по кредитам {loanPayment.toFixed(2)} · Итог {net.toFixed(2)}{transfer !== undefined ? ` · Переводы ${transfer.toFixed(2)}` : ''}
              </div>
            )) : '—'
          ) : (
            <>Сумма: {totalAmounts.length ? totalAmounts.map(({ amount, currency }) => `${amount.toFixed(2)} ${currency}`).join(' · ') : '—'}</>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl3 border border-glass-border bg-glass backdrop-blur-lg shadow-glass overflow-hidden" style={{ transform: 'translateZ(0)' }}>
          <div className="max-h-[calc(100vh-220px)] overflow-y-auto px-6 py-7">
          <div className="space-y-6">
            <Select label="Что показать" value={whatToShow} onChange={(e) => setWhatToShow(e.target.value)}>
              <option value="all">Все операции</option>
              <option value="income">Доходы</option>
              <option value="expense">Расходы</option>
              <option value="transfer">Перемещения</option>
            </Select>

            {whatToShow === 'all' && (
              <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={excludeTransfers}
                  onChange={(e) => setExcludeTransfers(e.target.checked)}
                  className="h-4 w-4 rounded accent-[var(--accent-0)]"
                />
                Исключить переводы между счетами
              </label>
            )}

            <Select label="Как показать" value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="detailed">Детально</option>
              {whatToShow === 'expense' && (
                <option value="by_category">По категориям</option>
              )}
              {whatToShow === 'income' && (
                <option value="by_income_source">По источникам дохода</option>
              )}
            </Select>

            <Select label="Период" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>

            {period === 'custom' && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="С"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                  label="По"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            )}

            <Select label="Счета" value={locationMode} onChange={(e) => setLocationMode(e.target.value)}>
              <option value="all">Все</option>
              <option value="include">Только выбранные</option>
              <option value="exclude">Все кроме выбранных</option>
            </Select>
            {locationMode !== 'all' && (
              <CheckboxList
                label="Выберите счета"
                value={locationIds}
                onChange={setLocationIds}
                options={locationOptions}
              />
            )}

            <Select label="Категории" value={categoryMode} onChange={(e) => setCategoryMode(e.target.value)}>
              <option value="all">Все</option>
              <option value="include">Только выбранные</option>
              <option value="exclude">Все кроме выбранных</option>
            </Select>
            {categoryMode !== 'all' && (
              <GroupedCategoryCheckboxList
                label="Выберите категории"
                value={categoryIds}
                onChange={setCategoryIds}
                categories={categories}
              />
            )}

            <Select label="Теги" value={tagMode} onChange={(e) => setTagMode(e.target.value)}>
              <option value="all">Все</option>
              <option value="include">Только выбранные</option>
              <option value="exclude">Все кроме выбранных</option>
            </Select>
            {tagMode !== 'all' && (
              <CheckboxList
                label="Выберите теги"
                value={tagIds}
                onChange={setTagIds}
                options={tagOptions}
              />
            )}

            <Input
              label="Комментарий"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Можно вводить часть слова"
            />

            <Select label="Сумма" value={amountMode} onChange={(e) => setAmountMode(e.target.value)}>
              {AMOUNT_MODES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>

            {amountMode === 'between' ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="От"
                  type="number"
                  value={amountMin}
                  onChange={(e) => setAmountMin(e.target.value)}
                />
                <Input
                  label="До"
                  type="number"
                  value={amountMax}
                  onChange={(e) => setAmountMax(e.target.value)}
                />
              </div>
            ) : amountMode !== 'any' ? (
              <Input
                label="Значение"
                type="number"
                value={amountValue}
                onChange={(e) => setAmountValue(e.target.value)}
              />
            ) : (
              <div className="h-12" />
            )}

            <div className="flex items-center justify-between">
              {error && <p className="text-sm text-accent">{error}</p>}
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? 'Формирование...' : 'Показать отчет'}
              </Button>
            </div>
          </div>
          </div>
        </div>

        <div className="space-y-6">


          {format === 'detailed' && (
        <Card>
          <div className="space-y-6">
            {groupedByDate.length === 0 && (
              <p className="text-sm text-gray-600 dark:text-slate-300">Нет данных для отображения</p>
            )}

            {groupedByDate.map(([dateKey, items]) => (
              <div key={dateKey}>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">
                  {new Date(dateKey).toLocaleDateString('ru-RU')}
                </h3>
                <div className="mt-3 space-y-2">
                  {items.map((tx) => (
                    <div key={tx.id} className="rounded-lg border border-white/40 bg-white/60 px-4 py-3 shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-slate-100">
                            {renderTransactionTitle(tx)}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-slate-300">{renderSource(tx)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-base font-semibold text-gray-800 dark:text-slate-100">{Number(tx.amount).toFixed(2)} {getTransactionCurrency(tx)}</p>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-600 dark:text-slate-300">
                        <span className="mr-3">Теги: {tx.tags?.length ? tx.tags.map((tag) => tag.name).join(', ') : '—'}</span>
                        <span>Комментарий: {tx.description || '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {format === 'by_category' && (
        <Card>
          <div className="space-y-4">
            {summary.length === 0 && (
              <p className="text-sm text-gray-600 dark:text-slate-300">Нет данных для отображения</p>
            )}
            {summary.map((row) => {
              const rowKey = `${row.id}-${row.currency || 'unknown'}`;
              return <div key={rowKey} className="rounded-lg border border-white/40 bg-white/60 p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-slate-100">
                      {row.name || 'Без категории'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent dark:text-accent dark:hover:text-accent"
                    onClick={() => setExpandedCategoryId(expandedCategoryId === rowKey ? null : rowKey)}
                  >
                    {Number(row.total_amount || 0).toFixed(2)} {row.currency || '—'}
                    {expandedCategoryId === rowKey ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>

                {expandedCategoryId === rowKey && (
                  <div className="mt-3 space-y-2">
                    {categoryTransactions.length === 0 && (
                      <p className="text-xs text-gray-600 dark:text-slate-300">Нет операций по этой категории</p>
                    )}
                    {categoryReportAllocations(categoryTransactions, row.id, row.currency || '—').map((allocation) => (
                      <div key={`${allocation.id}-${allocation.allocationId || 'header'}`} className="rounded-md border border-white/40 bg-white/70 px-3 py-2 text-xs text-gray-700 dark:border-white/10 dark:bg-slate-900/70 dark:text-slate-200">
                        <div className="flex items-center justify-between">
                          <span>{new Date(allocation.date).toLocaleDateString('ru-RU')}</span>
                          <span className="font-semibold">{Number(allocation.allocationAmount).toFixed(2)} {getTransactionCurrency(allocation)}</span>
                        </div>
                        <div className="mt-1">{allocation.allocationDescription || allocation.description || '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>;
            })}
          </div>
        </Card>
      )}

      {format === 'by_income_source' && (
        <Card>
          <div className="space-y-4">
            {summary.length === 0 && (
              <p className="text-sm text-gray-600 dark:text-slate-300">Нет данных для отображения</p>
            )}
            {summary.map((row) => {
              const rowKey = `${row.id}-${row.currency || 'unknown'}`;
              return <div key={rowKey} className="rounded-lg border border-white/40 bg-white/60 p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-slate-100">
                      {row.name || 'Без источника'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent dark:text-accent dark:hover:text-accent"
                    onClick={() => setExpandedCategoryId(expandedCategoryId === rowKey ? null : rowKey)}
                  >
                    {Number(row.total_amount || 0).toFixed(2)} {row.currency || '—'}
                    {expandedCategoryId === rowKey ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>

                {expandedCategoryId === rowKey && (
                  <div className="mt-3 space-y-2">
                    {transactions.filter((tx) => String(tx.income_source_id) === String(row.id) && getTransactionCurrency(tx) === (row.currency || '—')).length === 0 && (
                      <p className="text-xs text-gray-600 dark:text-slate-300">Нет операций по этому источнику</p>
                    )}
                    {transactions
                      .filter((tx) => String(tx.income_source_id) === String(row.id) && getTransactionCurrency(tx) === (row.currency || '—'))
                      .map((tx) => (
                        <div key={tx.id} className="rounded-md border border-white/40 bg-white/70 px-3 py-2 text-xs text-gray-700 dark:border-white/10 dark:bg-slate-900/70 dark:text-slate-200">
                          <div className="flex items-center justify-between">
                            <span>{new Date(tx.date).toLocaleDateString('ru-RU')}</span>
                            <span className="font-semibold">{Number(tx.amount).toFixed(2)} {getTransactionCurrency(tx)}</span>
                          </div>
                          <div className="mt-1">{tx.description || '—'}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>;
            })}
          </div>
        </Card>
      )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
